import { Fragment, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  ackRows, ackVerdict, searchResultsLabel, VERDICT_LABEL, VERDICT_TONE, type TransactionAck,
} from '../../lib/transactions/ackView';
import { api, ApiError } from '../../lib/api/client';
import { useIsSiteAdmin } from '../../lib/api/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faUser } from '@fortawesome/free-solid-svg-icons';
import { fmtDate, fmtDateTime, stateTone, typeLabel } from '../../lib/transactions/display';
import { MigrateLegacyPanel } from './MigrateLegacy';
import { DevicePanel } from './DevicePanel';

interface AccountRow {
  accountId: string;
  name: string;
  status: string;
  email: string | null;
  stripeCustomerId: string | null;
  createdOn: string | null;
}
interface AccountPage { items: AccountRow[]; cursor: string | null; exactEmail?: boolean }
interface DocePrice {
  priceId: string; productName: string | null; nickname: string | null;
  unitAmount: number | null; currency: string; usageType: string | null; recommended: boolean;
}

const PAGE_SIZE = 50;
/** Stripe deep link. The account segment keeps the link correct for staff signed into several accounts. */
const STRIPE_ACCT = 'acct_1KKRtDEfulr5MssV';
const stripeCustomerUrl = (id: string) => `https://dashboard.stripe.com/${STRIPE_ACCT}/customers/${id}`;
const money = (cents: number, currency = 'usd') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);

interface CustomerAddress {
  line1?: string; line2?: string; city?: string; state?: string; postalCode?: string; country?: string;
  name?: string; phone?: string;
}

interface TxnRow {
  transactionId: string; tcn: string | null; captureType: string; state: string;
  createdOn: string | null; billingMode: string | null;
  /** Set when the customer removed it from their own list. Staff still see it — see the admin route. */
  deletedOn: string | null;
  firstName: string | null; lastName: string | null;
  /** Why the FBI channel refused it, when it did. */
  ackReason: string | null;
  /** The whole acknowledgment from the FBI channel, when one has come back. */
  ack: TransactionAck | null;
  resubmits: number;
  /** What can be downloaded. Presence only — the keys stay server-side. */
  hasStoredEft: boolean; hasPhoto: boolean; hasEbts: boolean;
  /** Which remedy this failure needs, decided from the acknowledgment rather than the state. */
  remedy: { canRetry: boolean; canCorrect: boolean; suggestedEcn?: string; reason: string; outcomeUnknown?: boolean };
  correctedByTcn: string | null;
  correctionOfTcn: string | null;
  ecn: string | null;
}
interface TxnPage { items: TxnRow[]; cursor: string | null }

/** One descriptive field of a submitted EFT, as the remedy editor shows it. */
interface Type2FieldRow { location: string; value: string; label?: string }
/** A field the remedy will change, as the server reports it back for confirmation. */
interface Type2Change { location: string; from: string; to: string }

/** Just what the authorization toggles need off /admin/workflows. */
interface GatedWorkflow {
  captureType: string; label: string; tag: string; enabled: boolean; requiresAuthorization?: boolean;
}

interface AccountDetail {
  account: {
    accountId: string; name: string; status: string; stripeCustomerId: string | null;
    swftEnabled: boolean; doceEnabled: boolean; doceUserId: string | null;
    /** Workflows gated by `requiresAuthorization` beyond the two with their own switches. */
    authorizedCaptureTypes: string[];
    doceAttorneyAcct: boolean; storageActive: boolean; createdOn: string | null;
    physicalAddress: CustomerAddress | null; billingAddress: CustomerAddress | null;
  };
  users: { userId: string; email: string; role: string; active: boolean }[];
  subscriptions: { subscriptionId: string; planName: string | null; captureType: string | null; captureTypes: string[]; billingMode: string; quantity: number; status: string }[];
  devices: { deviceId: string; serialNumber: string; name: string | null; restricted: boolean }[];
  licenses: { licenseId: string; status: string; deviceId: string | null }[];
}

/** Detail route for one customer. */
export const customerPath = (accountId: string) => `/app/admin/customers/${accountId}`;

export function Customers() {
  const isAdmin = useIsSiteAdmin();
  const navigate = useNavigate();
  // The search term lives in the URL so that returning from a customer's page — via the back link or
  // the browser's Back button — restores the list the operator was looking at rather than resetting to
  // the unfiltered first page.
  const [params, setParams] = useSearchParams();
  const urlTerm = params.get('q') ?? '';
  const [filter, setFilter] = useState(urlTerm);
  // Debounced so each keystroke doesn't start a paged query against thousands of customers.
  const [term, setTerm] = useState(urlTerm);
  useEffect(() => {
    const t = setTimeout(() => {
      const next = filter.trim();
      setTerm(next);
      // replace: typing shouldn't stack history entries the operator has to click back through.
      setParams(next ? { q: next } : {}, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [filter, setParams]);

  const accounts = useInfiniteQuery({
    queryKey: ['admin-accounts', term],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (pageParam) qs.set('cursor', pageParam);
      if (term) qs.set('q', term);
      return api.get<AccountPage>(`/admin/accounts?${qs.toString()}`);
    },
    // A filtered page can come back short (or empty) and still have a cursor, because DynamoDB applies
    // the filter after reading — so "no items" must not be mistaken for "end of list".
    getNextPageParam: (last) => last.cursor ?? undefined,
    enabled: isAdmin,
  });

  if (!isAdmin) {
    return <div className="panel empty"><h3>Restricted</h3><p>Customer management is available to Lakota platform staff only.</p></div>;
  }

  const rows = accounts.data?.pages.flatMap((p) => p.items) ?? [];
  const exactEmail = accounts.data?.pages.some((p) => p.exactEmail) ?? false;

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Customer management</h1>
          <div className="sub">Select a customer to provision SWFT and FBI (DOCE) access, adjust account credit, and review their account.</div>
        </div>
      </div>

      <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: 12 }}>
          <input className="input" placeholder="Search name, email, city, state, or a TCN…" value={filter}
            onChange={(e) => setFilter(e.target.value)} />
          <span className="field-help" style={{ marginTop: 6, display: 'block' }}>
            {exactEmail
              ? 'Email lookup — exact match only.'
              : 'Name matches as you type; enter a full email address to look a customer up directly.'}
          </span>
        </div>
        {accounts.isLoading ? (
          <p className="loading-screen">// loading customers…</p>
        ) : (
          <table className="tx-table">
            <thead><tr><th>Customer</th><th>Email</th><th>Created</th><th>Status</th></tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.accountId} className="row-click" onClick={() => navigate(customerPath(a.accountId))}>
                  <td>
                    {/* A real link as well as the row click, so the name can be opened in a new tab and
                        reached by keyboard. stopPropagation keeps the row handler from firing too. */}
                    <Link className="row-link" to={customerPath(a.accountId)} onClick={(e) => e.stopPropagation()}>
                      {a.name}
                    </Link>
                    <div className="acct-code">{a.accountId}</div>
                  </td>
                  <td style={{ overflowWrap: 'anywhere' }}>{a.email ?? <span style={{ color: 'var(--text-faint)' }}>—</span>}</td>
                  <td>{a.createdOn ? new Date(a.createdOn).toLocaleDateString() : '—'}</td>
                  <td><span className={`status-dot ${a.status === 'ACTIVE' ? 'ok' : 'warn'}`}>{a.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {accounts.hasNextPage && (
          <div style={{ padding: 12, borderTop: '1px solid var(--border)' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => void accounts.fetchNextPage()}
              disabled={accounts.isFetchingNextPage}>
              {accounts.isFetchingNextPage ? 'Loading…' : `Load ${PAGE_SIZE} more`}
            </button>
            <span className="field-help" style={{ marginLeft: 10 }}>{rows.length} loaded</span>
          </div>
        )}
        {!accounts.isLoading && rows.length === 0 && (
          <div className="panel empty" style={{ boxShadow: 'none' }}><h3>No customers matched</h3></div>
        )}
      </div>
    </div>
  );
}

/**
 * Route component for /app/admin/customers/:accountId.
 *
 * Separate from the list so the table gets the full page width — with 2,000+ customers the old
 * side-by-side layout left the list too narrow to scan and the detail cramped.
 */
export function CustomerDetailPage() {
  const isAdmin = useIsSiteAdmin();
  const { accountId = '' } = useParams<{ accountId: string }>();

  if (!isAdmin) {
    return <div className="panel empty"><h3>Restricted</h3><p>Customer management is available to Lakota platform staff only.</p></div>;
  }
  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Customer</h1>
          <div className="sub">
            <Link to="/app/admin/customers" className="track-link">← Customer management</Link>
            {' · '}<span className="mono">{accountId}</span>
          </div>
        </div>
      </div>
      <CustomerDetail accountId={accountId} />
    </div>
  );
}

/**
 * Adjust the customer's Stripe credit balance. This credits FUTURE invoices — it is not a card refund,
 * so no money moves here. Stripe treats a negative balance as credit; the API takes a positive
 * "credit" and negates it, so this form talks in plain dollars.
 */
function CreditPanel({ accountId, stripeCustomerId }: { accountId: string; stripeCustomerId: string | null }) {
  const [dollars, setDollars] = useState('');
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const cents = Math.round(Number(dollars) * 100);
  const valid = Number.isFinite(cents) && Number.isInteger(cents) && cents !== 0;

  const apply = useMutation({
    mutationFn: () => api.post<{ creditedCents: number; endingBalanceCents: number; currency: string }>(
      `/admin/accounts/${accountId}/credit`,
      { creditCents: cents, ...(note.trim() ? { description: note.trim() } : {}) },
    ),
    onSuccess: (r) => {
      setMsg(`Applied ${money(r.creditedCents, r.currency)} credit — balance now ${money(-r.endingBalanceCents, r.currency)} available.`);
      setErr(null); setDollars(''); setNote(''); setConfirming(false);
    },
    onError: (e) => { setErr(e instanceof ApiError ? e.message : 'Could not apply the credit'); setConfirming(false); },
  });

  if (!stripeCustomerId) return null;
  return (
    <div className="panel">
      <div className="panel-head"><h3 style={{ margin: 0 }}>Account credit</h3></div>
      <p className="cart-note" style={{ textAlign: 'left', marginTop: 0 }}>
        // Credits the customer's Stripe balance, applied against future invoices. This is not a card
        refund — use Stripe directly to refund a specific payment.
      </p>
      <div className="inline-form" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label className="field" style={{ maxWidth: 160 }}>
          <span className="field-label">Credit (USD)</span>
          <input className="input" inputMode="decimal" placeholder="25.00" value={dollars}
            onChange={(e) => { setDollars(e.target.value); setConfirming(false); }} />
        </label>
        <label className="field" style={{ maxWidth: 260 }}>
          <span className="field-label">Note (optional)</span>
          <input className="input" placeholder="Goodwill for failed conversion" value={note}
            onChange={(e) => setNote(e.target.value)} />
        </label>
        {confirming ? (
          <>
            <span className="flow-hint">Apply {money(cents)} credit?</span>
            <button className="btn btn-primary btn-sm" disabled={apply.isPending} onClick={() => apply.mutate()}>
              {apply.isPending ? 'Applying…' : 'Confirm'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setConfirming(false)}>Cancel</button>
          </>
        ) : (
          // Confirm step on purpose: this writes to live billing and there is no undo in this UI.
          <button className="btn btn-ghost btn-sm" disabled={!valid} onClick={() => { setMsg(null); setErr(null); setConfirming(true); }}>
            Apply credit
          </button>
        )}
      </div>
      <span className="field-help">Negative amounts debit the customer instead.</span>
      {msg && <div className="banner-ok" style={{ marginTop: 12 }}>{msg}</div>}
      {err && <p className="cart-error">{err}</p>}
    </div>
  );
}

/** One line per populated address field, in postal order. Empty lines are dropped rather than shown blank. */
function AddressLines({ a }: { a: CustomerAddress }) {
  const cityStateZip = [[a.city, a.state].filter(Boolean).join(', '), a.postalCode].filter(Boolean).join(' ');
  const lines = [a.name, a.line1, a.line2, cityStateZip, a.country, a.phone ? `Phone: ${a.phone}` : ''].filter(Boolean);
  if (!lines.length) return <span style={{ color: 'var(--text-faint)' }}>—</span>;
  return <>{lines.map((l, i) => <div key={i}>{l}</div>)}</>;
}

/** True when the two addresses are the same place — the common case, worth saying rather than repeating. */
function sameAddress(x: CustomerAddress | null, y: CustomerAddress | null): boolean {
  if (!x || !y) return false;
  const norm = (a: CustomerAddress) =>
    [a.line1, a.line2, a.city, a.state, a.postalCode, a.country]
      .map((v) => (v ?? '').trim().toUpperCase()).join('|');
  return norm(x) === norm(y);
}

/**
 * The account's addresses. Physical is the one the store's ship-to prefills from and the ops map geocodes;
 * an account has no separate shipping address, so the label says so rather than implying one exists.
 */
function AddressPanel({ physical, billing }: { physical: CustomerAddress | null; billing: CustomerAddress | null }) {
  const same = sameAddress(physical, billing);
  return (
    <div className="panel">
      <div className="section-title" style={{ marginTop: 0 }}>Addresses</div>
      <div className="review-grid">
        <div className="review-row" style={{ alignItems: 'flex-start' }}>
          <span>Physical <span className="field-help">(ship-to default)</span></span>
          <strong style={{ textAlign: 'right' }}>
            {physical ? <AddressLines a={physical} /> : <span style={{ color: 'var(--text-faint)' }}>Not captured</span>}
          </strong>
        </div>
        <div className="review-row" style={{ alignItems: 'flex-start' }}>
          <span>Billing</span>
          <strong style={{ textAlign: 'right' }}>
            {same
              ? <span style={{ color: 'var(--text-dim)' }}>Same as physical</span>
              : billing ? <AddressLines a={billing} /> : <span style={{ color: 'var(--text-faint)' }}>Not captured</span>}
          </strong>
        </div>
      </div>
      <p className="field-help" style={{ marginBottom: 0 }}>
        Each order also stores the ship-to address used at the time, which may differ — see the order in
        Admin → Orders.
      </p>
    </div>
  );
}

/** One-click FBI Departmental Order subscription for a DOCE-enabled customer that has none. */
function DocePanel({ accountId, doceEnabled, hasDoceSub }: {
  accountId: string; doceEnabled: boolean; hasDoceSub: boolean;
}) {
  const qc = useQueryClient();
  const [priceId, setPriceId] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Only fetch the catalog when the action is actually available.
  const prices = useQuery({
    queryKey: ['doce-prices', accountId],
    queryFn: () => api.get<DocePrice[]>(`/admin/accounts/${accountId}/doce-prices`),
    enabled: doceEnabled && !hasDoceSub,
  });

  useEffect(() => {
    if (priceId || !prices.data?.length) return;
    setPriceId((prices.data.find((p) => p.recommended) ?? prices.data[0]!).priceId);
  }, [prices.data, priceId]);

  const create = useMutation({
    mutationFn: () => api.post<{ subscriptionId: string }>(`/admin/accounts/${accountId}/doce-subscription`, { priceId }),
    onSuccess: (r) => {
      setMsg(`Created ${r.subscriptionId}. It appears below once the Stripe webhook syncs it.`);
      setErr(null);
      qc.invalidateQueries({ queryKey: ['admin-account', accountId] });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not create the subscription'),
  });

  if (!doceEnabled) return null;
  if (hasDoceSub) {
    return (
      <div className="panel">
        <div className="panel-head"><h3 style={{ margin: 0 }}>FBI Departmental Order</h3></div>
        <p className="field-help" style={{ margin: 0 }}>This customer already has an active DOCE subscription.</p>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="panel-head"><h3 style={{ margin: 0 }}>FBI Departmental Order</h3></div>
      <p className="cart-note" style={{ textAlign: 'left', marginTop: 0 }}>
        // DOCE is enabled but this customer has no DOCE subscription, so their captures would be
        refused. Creating one bills them from the selected price.
      </p>
      <div className="inline-form" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label className="field" style={{ minWidth: 280 }}>
          <span className="field-label">Price</span>
          <select className="select" value={priceId} onChange={(e) => setPriceId(e.target.value)}>
            {(prices.data ?? []).map((p) => (
              <option key={p.priceId} value={p.priceId}>
                {[p.productName, p.nickname].filter(Boolean).join(' · ')}
                {p.unitAmount != null ? ` — ${money(p.unitAmount, p.currency)}` : ''}
                {p.usageType === 'metered' ? ' (metered)' : ''}
                {p.recommended ? ' ★' : ''}
              </option>
            ))}
          </select>
        </label>
        <button className="btn btn-primary btn-sm" disabled={!priceId || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? 'Creating…' : 'Create subscription'}
        </button>
      </div>
      {prices.isLoading && <span className="field-help">Loading DOCE prices…</span>}
      {prices.data?.length === 0 && <p className="cart-error">No active DOCE price found in Stripe.</p>}
      {msg && <div className="banner-ok" style={{ marginTop: 12 }}>{msg}</div>}
      {err && <p className="cart-error">{err}</p>}
    </div>
  );
}

/**
 * The customer's own transactions, newest first.
 *
 * Reads the staff route rather than GET /transactions, which is scoped to the caller's own account.
 * Paged, not filtered: support usually wants the last handful, and the customer-facing search already
 * exists for the customer themselves.
 */
/**
 * A DOCE remedy, with the confirm step in front of it.
 *
 * A correction goes to the REAL FBI channel and is by definition a second attempt at something already
 * rejected, so nothing is sent until an agent has seen exactly what will be sent. The server does the
 * deciding: it is asked to preview, returns the TCN it would mint and the ECN it would quote, and only
 * acts on a second call carrying `confirm`. The button never guesses.
 */
/**
 * What the FBI channel said about one submission.
 *
 * Read-only, and the first place in the console that shows a SUCCESSFUL acknowledgment at all — the row
 * previously surfaced only `ackReason`, and only on a failure, so a transaction that worked displayed
 * nothing and the question "did it arrive" needed a DynamoDB query.
 *
 * The verdict at the top is computed rather than read off any single field, because no single field carries
 * it: `response.error` holds the word "Successful" on a transaction that passed, and a router-side refusal
 * means the FBI never saw the file at all. See `lib/transactions/ackView`.
 */
function AckPanel({ ack }: { ack: TransactionAck }) {
  const verdict = ackVerdict(ack);
  const rows = ackRows(ack);
  const srf = searchResultsLabel(ack.response?.srf);

  return (
    <div className="ack-panel">
      <div className="ack-head">
        <span className={`status-dot ${VERDICT_TONE[verdict]}`}>{VERDICT_LABEL[verdict]}</span>
        {/* The plain-English reading of `srf`, which is otherwise a bare `f` or `t`. */}
        {srf && <span className="ack-srf">{srf}</span>}
      </div>

      {rows.length === 0 ? (
        <p className="field-help">// nothing has come back from the FBI channel for this submission yet</p>
      ) : (
        <dl className="ack-grid">
          {rows.map((r) => (
            <div key={r.label} className={`ack-row${r.wide ? ' ack-row-wide' : ''}`}>
              <dt>{r.label}</dt>
              <dd className={`mono${r.bad ? ' ack-bad' : ''}`}>{r.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {/*
        * Said outright, because it has already misled once: a passing background check was reported as
        * ERROR by code that treated this field's presence as failure.
        */}
      {ack.response?.error && !rows.find((r) => r.label === 'FBI verdict')?.bad && (
        <p className="field-help ack-note">
          The FBI writes its verdict into the field it calls <span className="mono">error</span> whether or not
          anything went wrong — <span className="mono">{ack.response.error}</span> here means it passed.
        </p>
      )}
    </div>
  );
}

function RemedyPanel({ accountId, txn, onDone }: { accountId: string; txn: TxnRow; onDone: () => void }) {
  const [mode, setMode] = useState<'idle' | 'retry' | 'correct'>('idle');
  const [ecn, setEcn] = useState(txn.remedy.suggestedEcn ?? '');
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [showFields, setShowFields] = useState(false);
  const [preview, setPreview] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const base = `/admin/accounts/${accountId}/transactions/${encodeURIComponent(txn.tcn ?? '')}`;

  /*
   * The descriptive fields as the FILE holds them, not as the transaction record does — the file is what the
   * FBI rejected, and its Type-2 record is the only complete account of what was sent. Fetched only when the
   * agent opens the editor, since most remedies change nothing.
   */
  const fields = useQuery({
    queryKey: ['admin-txn-fields', accountId, txn.tcn],
    queryFn: () => api.get<{ fields: Type2FieldRow[] }>(`${base}/fields`),
    enabled: showFields && Boolean(txn.tcn),
  });

  const changed = Object.entries(edits).filter(([loc, v]) => {
    const original = fields.data?.fields.find((f) => f.location === loc)?.value ?? '';
    return v !== original;
  });

  async function call(path: 'retry' | 'correct', confirm: boolean) {
    setBusy(true); setErr(null);
    try {
      const res = await api.post<Record<string, unknown>>(`${base}/${path}`, {
        ...(path === 'correct' ? { ecn } : {}),
        ...(changed.length ? { edits: Object.fromEntries(changed) } : {}),
        ...(confirm ? { confirm: true } : {}),
      });
      if (confirm) {
        setDone(path === 'correct'
          ? `Correction submitted as ${String(res['newTcn'])}. The original is now RESUBMITTED.`
          : `Re-sent under the same TCN (attempt ${String(res['attempt'])}).`);
        setPreview(null);
        onDone();
      } else {
        setPreview(res);
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  }

  if (done) return <div className="banner-ok" style={{ marginTop: 8 }}>{done}</div>;

  const previewChanges = (preview?.['changes'] as Type2Change[] | undefined) ?? [];

  return (
    <div className="remedy">
      <p className="remedy-why">{txn.remedy.reason}</p>

      {mode === 'idle' && (
        <div className="remedy-choice">
          {/* Both are offered when nothing says which failure it was, or when the FBI never answered. */}
          {txn.remedy.canRetry && (
            <button className="btn btn-ghost btn-sm" onClick={() => setMode('retry')}>
              Re-send this submission
            </button>
          )}
          {txn.remedy.canCorrect && (
            <button className="btn btn-ghost btn-sm" onClick={() => setMode('correct')}>
              Submit a correction (needs an ECN)
            </button>
          )}
        </div>
      )}

      {mode !== 'idle' && !preview && (
        <>
          {mode === 'correct' && (
            <div className="remedy-form">
              <label className="field">
                <span className="field-label">ECN from the FBI rejection</span>
                <input className="input mono" value={ecn} onChange={(e) => setEcn(e.target.value)}
                  placeholder="E2026215000000149873" maxLength={24} />
                <span className="field-help">
                  Twenty characters: E, year, day of the year, then the serial.
                  {txn.remedy.suggestedEcn && ' Pre-filled from the acknowledgment.'}
                </span>
              </label>
            </div>
          )}

          {/*
            * The correction itself. A rejection is usually one wrong value — 2.037 carrying the ATF reason on
            * a DOCE — and re-sending the same bytes only collects the same rejection. Optional, because a
            * delivery failure needs no edit at all.
            */}
          <div className="remedy-fields">
            {!showFields ? (
              <button className="btn btn-ghost btn-sm" onClick={() => setShowFields(true)}>
                Edit the descriptive fields…
              </button>
            ) : fields.isLoading ? (
              <p className="cart-note" style={{ textAlign: 'left' }}>// reading the submitted file…</p>
            ) : fields.error ? (
              <p className="cart-error">
                {fields.error instanceof ApiError ? fields.error.message : 'Could not read the submitted file'}
              </p>
            ) : (
              <>
                <p className="field-help" style={{ marginBottom: 8 }}>
                  Read from the submitted EFT. Changing a value rewrites that field and leaves everything else —
                  imagery included — exactly as it was. A field cannot be cleared: an empty Type-2 field is
                  itself a rejection.
                </p>
                <div className="remedy-field-scroll">
                  <table className="tx-table">
                    <thead><tr><th>Field</th><th>Name</th><th>Value</th></tr></thead>
                    <tbody>
                      {(fields.data?.fields ?? []).map((f) => {
                        const value = edits[f.location] ?? f.value;
                        const dirty = value !== f.value;
                        return (
                          <tr key={f.location} className={dirty ? 'row-selected' : undefined}>
                            <td className="mono">2.{f.location}</td>
                            <td>{f.label ?? '—'}</td>
                            <td>
                              <input className="input mono" value={value}
                                onChange={(e) => setEdits((p) => ({ ...p, [f.location]: e.target.value }))} />
                              {dirty && (
                                <div className="field-help">was <span className="mono">{f.value || '(empty)'}</span></div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          <div className="remedy-choice">
            <button className="btn btn-ghost btn-sm" onClick={() => { setMode('idle'); setErr(null); }}>Cancel</button>
            <button className="btn btn-primary btn-sm"
              disabled={busy || (mode === 'correct' && !ecn.trim())}
              onClick={() => void call(mode === 'correct' ? 'correct' : 'retry', false)}>
              {busy ? 'Checking…' : mode === 'correct' ? 'Review the correction' : 'Review the re-send'}
            </button>
          </div>
        </>
      )}

      {preview && (
        /* Everything that is about to reach the FBI, before it does. */
        <div className="remedy-confirm">
          <div className="remedy-facts">
            {mode === 'correct' ? (
              <>
                <div><span>New TCN</span><strong className="mono">{String(preview['newTcn'] ?? '')}</strong></div>
                <div><span>Replaces</span><strong className="mono">{String(preview['originalTcn'] ?? '')}</strong></div>
                <div><span>ECN → 1.010</span><strong className="mono">{String(preview['ecn'] ?? '')}</strong></div>
                <div><span>Size</span><strong>{Number(preview['bytes'] ?? 0).toLocaleString()} bytes</strong></div>
              </>
            ) : (
              <>
                <div><span>TCN</span><strong className="mono">{String(preview['tcn'] ?? '')}</strong></div>
                <div><span>Attempt</span><strong>{String(preview['attempt'] ?? '')}</strong></div>
              </>
            )}
            <div><span>Submitter</span><strong className="mono">{String(preview['submitterId'] ?? '—')}</strong></div>
            {/* Worth seeing either way: the channeler reads the applicant's email from this document. */}
            <div><span>Applicant JSON</span><strong>{preview['applicantJson'] ? 'included' : 'none on file'}</strong></div>
          </div>

          {previewChanges.length > 0 && (
            <div className="remedy-diff">
              <div className="section-title" style={{ marginTop: 0 }}>Field changes</div>
              {previewChanges.map((c) => (
                <div key={c.location} className="remedy-diff-row">
                  <span className="mono">2.{c.location}</span>
                  <span className="mono remedy-was">{c.from || '(empty)'}</span>
                  <span aria-hidden="true">→</span>
                  <strong className="mono">{c.to}</strong>
                </div>
              ))}
            </div>
          )}

          <p className="remedy-warn">
            This sends to the FBI channel{mode === 'correct' ? ' as a new transaction' : ''}. It cannot be recalled.
            {/*
              * Said plainly, because the state cannot say it: a submission stale in SUBMITTED may have been
              * processed and never acknowledged, and re-sending one of those is a duplicate.
              */}
            {mode === 'retry' && txn.remedy.outcomeUnknown &&
              ' The FBI never acknowledged this submission, so if they did process it this will be a duplicate' +
              ' — prefer a correction unless you know they never received it.'}
            {mode === 'retry' && previewChanges.length === 0 && ' The file is unchanged from the one already sent.'}
          </p>
          <div className="remedy-choice">
            <button className="btn btn-ghost btn-sm" onClick={() => setPreview(null)}>Back</button>
            <button className="btn btn-primary btn-sm" disabled={busy}
              onClick={() => void call(mode === 'correct' ? 'correct' : 'retry', true)}>
              {busy ? 'Sending…' : mode === 'correct' ? 'Send the correction' : 'Re-send'}
            </button>
          </div>
        </div>
      )}

      {err && <p className="cart-error" style={{ marginTop: 8 }}>{err}</p>}
    </div>
  );
}

function CustomerTransactions({ accountId }: { accountId: string }) {
  const txns = useInfiniteQuery({
    queryKey: ['admin-account-txns', accountId],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams({ limit: '25' });
      if (pageParam) qs.set('cursor', pageParam);
      return api.get<TxnPage>(`/admin/accounts/${accountId}/transactions?${qs.toString()}`);
    },
    getNextPageParam: (last) => last.cursor ?? undefined,
  });
  const rows = txns.data?.pages.flatMap((p) => p.items) ?? [];
  const [open, setOpen] = useState<string | null>(null);
  const [dl, setDl] = useState<string | null>(null);
  const [dlErr, setDlErr] = useState<string | null>(null);

  /** Any stored artifact, by presigned URL. Support looks more than once, so nothing is consumed. */
  async function grab(t: TxnRow, artifact: 'eft' | 'ebts' | 'photo') {
    setDl(`${t.tcn}-${artifact === 'ebts' ? 'eft' : artifact}`);
    setDlErr(null);
    try {
      const res = await api.get<{ url: string; filename: string }>(
        `/admin/accounts/${accountId}/transactions/${encodeURIComponent(t.tcn ?? '')}/download?artifact=${artifact}`);
      const a = document.createElement('a');
      a.href = res.url; a.download = res.filename; a.rel = 'noopener';
      document.body.appendChild(a); a.click();
      setTimeout(() => a.remove(), 1500);
    } catch (e) {
      setDlErr(e instanceof ApiError ? e.message : 'Download failed');
    } finally {
      setDl(null);
    }
  }

  if (txns.isLoading) return <div className="panel"><p className="cart-note">// loading transactions…</p></div>;

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>Transactions</h3>
        {rows.length > 0 && <span className="field-help">{rows.length} loaded{txns.hasNextPage ? '+' : ''}</span>}
      </div>
      {dlErr && <p className="cart-error">{dlErr}</p>}
      {rows.length === 0 ? (
        <p className="cart-note" style={{ textAlign: 'left' }}>// this customer has no transactions</p>
      ) : (
        <>
          <table className="tx-table">
            <thead><tr><th>Created</th><th>Name</th><th>Type</th><th>TCN</th><th>Billing</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {rows.map((t) => (
                <Fragment key={t.transactionId}>
                {/* Dimmed, never hidden: this row is why a charge the customer cannot see still exists. */}
                <tr className={t.deletedOn ? 'tx-deleted' : undefined}>
                  {/* Local time, not just the date: a capture taken in the evening is stored as the NEXT
                      day in UTC, so a date alone disagreed with the day the operator remembers working. */}
                  <td className="tx-created">{fmtDateTime(t.createdOn)}</td>
                  <td>
                    {[t.lastName, t.firstName].filter(Boolean).join(', ') || '—'}
                    {t.deletedOn && (
                      <span className="pill tx-deleted-pill" title={`The customer deleted this on ${fmtDateTime(t.deletedOn)}. The record is kept 90 days so a charge can still be explained; the stored file was erased at once.`}>
                        Deleted
                      </span>
                    )}
                  </td>
                  <td>{typeLabel(t.captureType)}</td>
                  <td className="mono tx-tcn">{t.tcn ?? '—'}</td>
                  <td>{t.billingMode ?? '—'}</td>
                  <td>
                    <span className={`status-badge tone-${stateTone(t.state)}`}>{t.state}</span>
                    {/* Why a submission failed is the first thing support is asked about. */}
                    {t.ackReason && <div className="tx-fail-reason">{t.ackReason}</div>}
                    {t.resubmits > 0 && <div className="tx-fail-reason">Resubmitted {t.resubmits}×</div>}
                    {/* The date matters as much as the fact: it is what lines a deletion up against a charge. */}
                    {t.deletedOn && (
                      <div className="tx-fail-reason">Deleted by the customer {fmtDateTime(t.deletedOn)}</div>
                    )}
                    {/* The correction link, both ways, so either end explains itself. */}
                    {t.correctedByTcn && (
                      <div className="tx-fail-reason">Superseded by <span className="mono">{t.correctedByTcn}</span></div>
                    )}
                    {t.correctionOfTcn && (
                      <div className="tx-fail-reason">
                        Corrects <span className="mono">{t.correctionOfTcn}</span>
                        {t.ecn && <> · ECN <span className="mono">{t.ecn}</span></>}
                      </div>
                    )}
                  </td>
                  <td className="tx-actions">
                    {/* Only what exists. A button for an artifact that was never stored is a dead end. */}
                    {(t.hasStoredEft || t.hasEbts) && (
                      <button className="btn btn-ghost btn-icon btn-sm" title="Download the EFT"
                        aria-label="Download EFT" disabled={dl === `${t.tcn}-eft`}
                        onClick={() => void grab(t, t.hasStoredEft ? 'eft' : 'ebts')}>
                        <FontAwesomeIcon icon={faDownload} />
                      </button>
                    )}
                    {t.hasPhoto && (
                      <button className="btn btn-ghost btn-icon btn-sm" title="Download the photograph"
                        aria-label="Download photograph" disabled={dl === `${t.tcn}-photo`}
                        onClick={() => void grab(t, 'photo')}>
                        <FontAwesomeIcon icon={faUser} />
                      </button>
                    )}
                    {/* Opens for an acknowledgment as well as a remedy. Gating it on the remedy alone meant a
                        SUCCESSFUL submission had nothing to open, so "did this reach the FBI, and what came
                        back" could only be answered with a database query. */}
                    {(t.remedy.canRetry || t.remedy.canCorrect || t.ack) && (
                      <button className="btn btn-ghost btn-sm"
                        onClick={() => setOpen(open === t.transactionId ? null : t.transactionId)}>
                        {open === t.transactionId ? 'Close'
                          : (t.remedy.canRetry || t.remedy.canCorrect) ? 'Remedy' : 'FBI response'}
                      </button>
                    )}
                  </td>
                </tr>
                {open === t.transactionId && (
                  <tr>
                    <td colSpan={7} style={{ background: 'var(--surface-2)' }}>
                      {t.ack && <AckPanel ack={t.ack} />}
                      {(t.remedy.canRetry || t.remedy.canCorrect) && (
                        <RemedyPanel accountId={accountId} txn={t} onDone={() => void txns.refetch()} />
                      )}
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
          {txns.hasNextPage && (
            <button className="btn btn-ghost" style={{ marginTop: 12 }}
              disabled={txns.isFetchingNextPage} onClick={() => void txns.fetchNextPage()}>
              {txns.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          )}
        </>
      )}
    </div>
  );
}

const TABS = [
  ['overview', 'Overview'],
  ['billing', 'Billing'],
  ['devices', 'Devices'],
  ['team', 'Team'],
  ['transactions', 'Transactions'],
] as const;
type Tab = (typeof TABS)[number][0];

/**
 * One customer, everything staff can see or change about them.
 *
 * Tabbed rather than a single column of panels: this page accumulated provisioning, addresses, users,
 * DOCE, credit, subscriptions, licenses and now transaction history, and stacking them meant scrolling
 * past six panels to reach the one being worked on. Identity and the counts staff check first stay
 * pinned in the header above the tabs, so they are never the thing that scrolled away.
 *
 * The tab lives in the URL so a link to a customer can point at the tab that matters, and Back steps
 * through tabs the way the operator navigated them.
 */
function CustomerDetail({ accountId }: { accountId: string }) {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: Tab = TABS.some(([t]) => t === raw) ? (raw as Tab) : 'overview';
  const detail = useQuery({
    queryKey: ['admin-account', accountId],
    queryFn: () => api.get<AccountDetail>(`/admin/accounts/${accountId}`),
  });

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/admin/accounts/${accountId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-account', accountId] });
      qc.invalidateQueries({ queryKey: ['admin-accounts'] });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Update failed'),
  });

  if (detail.isLoading) return <p className="loading-screen">// loading customer…</p>;
  if (!detail.data) {
    return (
      <div className="panel empty">
        <h3>Not found</h3>
        <p>No customer with this id. <Link to="/app/admin/customers" className="track-link">Back to customers</Link></p>
      </div>
    );
  }
  const a = detail.data.account;
  const activeSubs = detail.data.subscriptions.filter((s) => s.status === 'ACTIVE').length;
  const allocated = detail.data.licenses.filter((l) => l.status === 'ALLOCATED').length;

  return (
    <div className="stack">
      <div className="panel cust-head">
        <div className="cust-head-top">
          <div>
            <h3>{a.name}</h3>
            <div className="acct-code">{a.accountId}</div>
          </div>
          <div className="cust-head-meta">
            <span className={`status-dot ${a.status === 'ACTIVE' ? 'ok' : 'warn'}`}>{a.status}</span>
            {a.stripeCustomerId ? (
              <a className="track-link" href={stripeCustomerUrl(a.stripeCustomerId)} target="_blank" rel="noreferrer">
                Open in Stripe ↗
              </a>
            ) : (
              <span className="field-help">No Stripe customer linked</span>
            )}
          </div>
        </div>
        <div className="cust-head-facts">
          <div className="cust-fact"><span className="cust-fact-num">{detail.data.users.length}</span><span className="cust-fact-lbl">Users</span></div>
          <div className="cust-fact"><span className="cust-fact-num">{activeSubs}</span><span className="cust-fact-lbl">Active plans</span></div>
          <div className="cust-fact"><span className="cust-fact-num">{detail.data.devices.length}</span><span className="cust-fact-lbl">Devices</span></div>
          <div className="cust-fact"><span className="cust-fact-num">{allocated}/{detail.data.licenses.length}</span><span className="cust-fact-lbl">Seats allocated</span></div>
          <div className="cust-fact"><span className="cust-fact-num">{fmtDate(a.createdOn)}</span><span className="cust-fact-lbl">Customer since</span></div>
        </div>
        <div className="cust-head-flags">
          <span className={`pill ${a.swftEnabled ? 'pill-scan' : 'pill-off'}`}>SWFT {a.swftEnabled ? 'on' : 'off'}</span>
          <span className={`pill ${a.doceEnabled ? 'pill-scan' : 'pill-off'}`}>DOCE {a.doceEnabled ? 'on' : 'off'}</span>
          <span className={`pill ${a.storageActive ? 'pill-scan' : 'pill-off'}`}>Storage {a.storageActive ? 'on' : 'off'}</span>
        </div>
      </div>

      <div className="seg cust-tabs">
        {TABS.map(([value, label]) => (
          <button key={value} className={`seg-btn${tab === value ? ' is-active' : ''}`}
            onClick={() => setParams(value === 'overview' ? {} : { tab: value })}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <>
          <div className="panel">
            <div className="section-title" style={{ marginTop: 0 }}>Transaction provisioning</div>
            <p className="cart-note" style={{ textAlign: 'left', marginTop: 0 }}>
              // SWFT and FBI DOCE are gated independently. Customers can't see a workflow until it's enabled here.
            </p>
            <div className="provision-row">
              <Toggle
                label="SWFT transactions"
                desc="DoD SWFT live-scan capture &amp; card conversion"
                on={a.swftEnabled}
                busy={patch.isPending}
                onToggle={() => { setErr(null); patch.mutate({ swftEnabled: !a.swftEnabled }); }}
              />
              <Toggle
                label="FBI DOCE transactions"
                desc="Departmental Order submission to the FBI channel"
                on={a.doceEnabled}
                busy={patch.isPending}
                onToggle={() => { setErr(null); patch.mutate({ doceEnabled: !a.doceEnabled }); }}
              />
            </div>

            {a.doceEnabled && <DoceConfig detail={a} onSave={(b) => { setErr(null); patch.mutate(b); }} busy={patch.isPending} />}

            {/*
              * Everything else that requires pre-authorization, listed from the workflow registry rather
              * than hardcoded — so a workflow a site admin adds can be turned on without a deploy.
              */}
            <GatedWorkflows
              authorized={a.authorizedCaptureTypes ?? []}
              busy={patch.isPending}
              onToggle={(next) => { setErr(null); patch.mutate({ authorizedCaptureTypes: next }); }}
            />

            <div className="section-title" style={{ marginTop: 20 }}>Account status</div>
            <button
              className={`btn ${a.status === 'ACTIVE' ? 'btn-ghost' : 'btn-primary'}`}
              disabled={patch.isPending}
              onClick={() => { setErr(null); patch.mutate({ status: a.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE' }); }}
            >
              {a.status === 'ACTIVE' ? 'Suspend account' : 'Reactivate account'}
            </button>
            {err && <p className="cart-error" style={{ marginTop: 12 }}>{err}</p>}
          </div>

          <AddressPanel physical={a.physicalAddress} billing={a.billingAddress} />

          <MigrateLegacyPanel accountId={accountId} accountName={a.name} />
        </>
      )}

      {tab === 'billing' && (
        <>
          <div className="panel">
            <div className="section-title" style={{ marginTop: 0 }}>Subscriptions ({detail.data.subscriptions.length})</div>
            {detail.data.subscriptions.length === 0 ? <p className="cart-note" style={{ textAlign: 'left' }}>// none</p> : (
              <table className="tx-table">
                <thead><tr><th>Plan</th><th>Type</th><th>Billing</th><th>Seats</th><th>Status</th></tr></thead>
                <tbody>
                  {detail.data.subscriptions.map((s) => (
                    <tr key={s.subscriptionId}>
                      <td>{s.planName ?? '—'}</td>
                      {/* captureTypes is what actually distinguishes an EFT plan from a card-conversion one;
                          captureType (singular) is unset on this catalog, so showing only it read as "—". */}
                      <td>{s.captureTypes?.length ? s.captureTypes.join(', ') : (s.captureType ?? '—')}</td>
                      <td>{s.billingMode}</td><td>{s.quantity}</td><td>{s.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <DocePanel
            accountId={accountId}
            doceEnabled={Boolean(a.doceEnabled)}
            hasDoceSub={detail.data.subscriptions.some(
              // Mirrors subscriptionEntitles server-side: a plan can be typed either way.
              (sub) => sub.status === 'ACTIVE' && (sub.captureType === 'doce' || sub.captureTypes.includes('doce')),
            )}
          />

          <CreditPanel accountId={accountId} stripeCustomerId={a.stripeCustomerId} />
        </>
      )}

      {tab === 'devices' && <DevicePanel accountId={accountId} />}

      {tab === 'team' && (
        <div className="panel">
          <div className="section-title" style={{ marginTop: 0 }}>Users ({detail.data.users.length})</div>
          <table className="tx-table">
            <thead><tr><th>Email</th><th>Role</th><th>Active</th></tr></thead>
            <tbody>
              {detail.data.users.map((u) => (
                <tr key={u.userId}><td>{u.email}</td><td>{u.role}</td><td>{u.active ? 'Yes' : 'No'}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'transactions' && <CustomerTransactions accountId={accountId} />}
    </div>
  );
}

function DoceConfig({ detail, onSave, busy }: {
  detail: AccountDetail['account'];
  onSave: (b: Record<string, unknown>) => void;
  busy: boolean;
}) {
  const [userId, setUserId] = useState(detail.doceUserId ?? '');
  const [attorney, setAttorney] = useState(detail.doceAttorneyAcct);
  const dirty = userId !== (detail.doceUserId ?? '') || attorney !== detail.doceAttorneyAcct;
  return (
    <div className="panel-inset" style={{ marginTop: 14 }}>
      <div className="field-label">FBI DOCE routing identity</div>
      <div className="inline-form" style={{ marginTop: 8 }}>
        <input className="input" placeholder="DOCE submitter ID" value={userId} onChange={(e) => setUserId(e.target.value)} />
        <label className="checkbox">
          <input type="checkbox" checked={attorney} onChange={(e) => setAttorney(e.target.checked)} />
          <span>Attorney account</span>
        </label>
        <button className="btn btn-primary btn-sm" disabled={!dirty || busy} onClick={() => onSave({ doceUserId: userId.trim(), doceAttorneyAcct: attorney })}>
          Save
        </button>
      </div>
    </div>
  );
}

/**
 * Per-account authorization for workflows that require it, other than SWFT and DOCE.
 *
 * Those two keep their own switches — their flags are what live accounts carry, and DOCE's reveals its
 * submitter fields. Anything else is read from the registry and stored in `authorizedCaptureTypes`, so
 * adding a gated workflow needs no column, no migration and no change here.
 */
function GatedWorkflows({ authorized, busy, onToggle }: {
  authorized: string[]; busy: boolean; onToggle: (next: string[]) => void;
}) {
  const isAdmin = useIsSiteAdmin();
  const wfs = useQuery({
    queryKey: ['admin-workflows'],
    queryFn: () => api.get<GatedWorkflow[]>('/admin/workflows'),
    enabled: isAdmin,
  });
  const LEGACY = ['swft', 'conv-swft', 'doce'];
  const gated = (wfs.data ?? []).filter((w) => w.enabled && w.requiresAuthorization && !LEGACY.includes(w.captureType));
  /*
   * An authorization for a workflow that has since been removed or ungated would otherwise be invisible
   * and unremovable, and it is still being sent back on every save. Shown so it can be cleared.
   */
  const orphans = authorized.filter((t) => !gated.some((w) => w.captureType === t) && !LEGACY.includes(t));
  if (!gated.length && !orphans.length) return null;

  const flip = (t: string) =>
    onToggle(authorized.includes(t) ? authorized.filter((x) => x !== t) : [...authorized, t]);

  return (
    <>
      <div className="section-title" style={{ marginTop: 20 }}>Other gated workflows</div>
      <div className="provision-row">
        {gated.map((w) => (
          <Toggle
            key={w.captureType}
            label={w.label || w.captureType}
            desc={w.tag || w.captureType}
            on={authorized.includes(w.captureType)}
            busy={busy}
            onToggle={() => flip(w.captureType)}
          />
        ))}
        {orphans.map((t) => (
          <Toggle key={t} label={t} desc="No longer a gated workflow — turn off to clear" on busy={busy} onToggle={() => flip(t)} />
        ))}
      </div>
    </>
  );
}

function Toggle({ label, desc, on, busy, onToggle }: {
  label: string; desc: string; on: boolean; busy: boolean; onToggle: () => void;
}) {
  return (
    <div className={`provision-card${on ? ' is-on' : ''}`}>
      <div>
        <div className="provision-label">{label}</div>
        <div className="provision-desc" dangerouslySetInnerHTML={{ __html: desc }} />
      </div>
      <button className={`switch${on ? ' on' : ''}`} onClick={onToggle} disabled={busy} aria-pressed={on} aria-label={`Toggle ${label}`}>
        <span className="switch-knob" />
      </button>
    </div>
  );
}
